import * as React from 'react';
import { ContextualMenu, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { TerminAttachment, updateTerminAttachment } from 'api/terminAttachment';
import { FormTextField } from 'formComponents/FormTextField';
import { SavingState } from 'api/savingState';
import { isEqual } from 'lodash';
import { logDifference } from 'utils/helper';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { useIsMounted } from 'hooks/useIsMounted';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { FormDatePicker } from 'formComponents/FormDatePicker';

const styles = mergeStyleSets({
    data: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: '150px 1fr max-content',
        gridColumnGap: 10,
        minHeight: 200,
    },
});

interface EditTerminAttachmentProps {
    terminAttachment: TerminAttachment;
    onClose: (refetch: boolean) => void;
}

export const EditTerminAttachment: React.FC<EditTerminAttachmentProps> = ({ terminAttachment, onClose }) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [terminAttachmentData, setTerminAttachmentData] = React.useState(terminAttachment);
    const isMounted = useIsMounted();

    React.useEffect(() => {
        if (savingState === SavingState.notChanged && !isEqual(terminAttachmentData, terminAttachment)) {
            setSavingState(SavingState.notSaved);
            logDifference('terminAttachment', terminAttachmentData, terminAttachment);
        }
    }, [savingState, terminAttachmentData, terminAttachment]);

    const save = React.useCallback(() => {
        if (!terminAttachmentData) {
            return;
        }

        if (savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            updateTerminAttachment(terminAttachmentData)
                .then(() => {
                    setSavingState(SavingState.saved);
                    if (isMounted) {
                        setSavingState(SavingState.notChanged);
                    }

                    onClose(true);
                })
                .catch((error) => {
                    console.error(error);
                    setSavingState(SavingState.error);
                });
        }
    }, [terminAttachmentData, isMounted, onClose, savingState]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title="Dokument bearbeiten"
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    layerProps: { eventBubblingEnabled: true },
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={1000}
                onDismiss={() => onClose(false)}
            >
                <form className={formStyles.form}>
                    <div className={styles.data}>
                        <FormDatePicker
                            label="Datum"
                            savingState={savingState}
                            value={terminAttachmentData.datum}
                            onChange={(newValue) => {
                                setTerminAttachmentData({
                                    ...terminAttachmentData,
                                    datum: newValue,
                                });
                            }}
                            options={{
                                required: true,
                            }}
                        />

                        <FormTextField
                            value={terminAttachmentData.text}
                            savingState={savingState}
                            options={{}}
                            onChange={(newValue): void => {
                                setTerminAttachmentData({
                                    ...terminAttachmentData,
                                    text: newValue as string,
                                });
                            }}
                            label={'Beschreibung - ' + terminAttachmentData.file}
                        />
                    </div>

                    <DialogFooter>
                        <DefaultButton
                            text="In OneDrive öffnen"
                            disabled={savingState !== SavingState.notChanged || !terminAttachment.oneDriveUrl}
                            onClick={() => {
                                if (terminAttachment.oneDriveUrl) {
                                    window.open(terminAttachment.oneDriveUrl);
                                }
                            }}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged}
                            />
                        )}
                    </DialogFooter>
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                </form>
            </Dialog>
        </>
    );
};
